import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, CalendarDays, BookOpen } from 'lucide-react';
import { useAuth } from '../../App';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container, CtaBand } from '../../components/public/PublicSections';
import { ContentBlocks, TableOfContents } from '../../components/public/ContentBlocks';
import { ArticleCard } from '../../components/public/ArticleCard';
import { ArticleShareBar } from '../../components/public/ArticleShareBar';
import { LAYOUT_COPY } from '../../seo/publicContent';
import { getArticle, listArticles, TIPS_COPY, ARTICLE_DATE } from '../../seo/articles';
import { pagePath, alternatesFor, absoluteUrl, SITE_NAME, SITE_URL, PUBLISHER, LOGO_PATH } from '../../seo/seoConfig.mjs';
import { breadcrumbSchema } from '../../seo/structuredData';
import NotFoundPage from './NotFoundPage';

const formatDate = (iso, lang) => new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });

const articleJsonLd = (a, lang) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: a.title,
  description: a.meta.description,
  inLanguage: lang,
  keywords: a.meta.keywords,
  articleSection: a.axisName,
  mainEntityOfPage: absoluteUrl(a.path),
  url: absoluteUrl(a.path),
  image: [absoluteUrl(a.cover)],
  datePublished: ARTICLE_DATE,
  dateModified: ARTICLE_DATE,
  timeRequired: `PT${a.minutes}M`,
  author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  publisher: { '@type': 'Organization', name: PUBLISHER, logo: { '@type': 'ImageObject', url: absoluteUrl(LOGO_PATH) } },
});

export default function PublicArticlePage({ lang, articleKey }) {
  const { user } = useAuth();
  const article = getArticle(articleKey, lang);
  if (!article) return <NotFoundPage />;

  const copy = TIPS_COPY[lang];
  const nav = LAYOUT_COPY[lang].nav;
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';
  const all = listArticles(lang);
  const index = all.findIndex(a => a.key === article.key);
  const prev = index > 0 ? all[index - 1] : null;
  const next = index < all.length - 1 ? all[index + 1] : null;
  const related = article.related.map(k => getArticle(k, lang)).filter(Boolean);
  const guideHref = `${pagePath('guide', lang)}#${article.guideAnchor}`;

  return (
    <PublicLayout lang={lang} pageKey={article.pageKey}>
      <Seo
        title={article.meta.title}
        description={article.meta.description}
        keywords={article.meta.keywords}
        path={article.path}
        lang={lang}
        alternates={alternatesFor(article.pageKey)}
        ogType="article"
        ogImage={article.cover}
        jsonLd={[
          breadcrumbSchema([
            { name: nav.home, path: pagePath('home', lang) },
            { name: nav.tips, path: pagePath('tips', lang) },
            { name: article.title, path: article.path },
          ]),
          articleJsonLd(article, lang),
        ]}
      />

      <article data-testid={`article-page-${article.key}`}>
        <header className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 15% 0%, rgba(243,179,61,0.12), transparent 55%)' }} />
          <Container className="relative pt-10 sm:pt-14 pb-8">
            <nav aria-label="Breadcrumb" className="text-xs text-white/45 flex items-center gap-2 flex-wrap" data-testid="article-breadcrumb">
              <Link to={pagePath('home', lang)} className="hover:text-[var(--lux-gold)] transition-colors duration-200">{nav.home}</Link>
              <span aria-hidden="true">/</span>
              <Link to={pagePath('tips', lang)} className="hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid="article-breadcrumb-tips">{nav.tips}</Link>
              <span aria-hidden="true">/</span>
              <span className="text-white/70 truncate max-w-[60vw]">{article.title}</span>
            </nav>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--lux-gold)] mb-3" data-testid="article-axis">{article.axisLabel}</p>
                <h1 className="font-display text-4xl sm:text-5xl leading-[1.08] text-white" data-testid="article-title">{article.title}</h1>
                <p className="mt-5 text-base sm:text-lg text-white/60 leading-relaxed" data-testid="article-excerpt">{article.excerpt}</p>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/45" data-testid="article-meta">
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--lux-gold)]" /> {article.minutes} {copy.readingTime}</span>
                  <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[var(--lux-gold)]" /> {copy.updated} {formatDate(ARTICLE_DATE, lang)}</span>
                  <span>{copy.author}</span>
                </div>
                <ArticleShareBar title={article.title} path={article.path} lang={lang} className="mt-6" testIdPrefix="article-share-top" />
              </div>
              <figure className="lg:col-span-5">
                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_12px_32px_rgba(0,0,0,0.55)] aspect-[3/2] bg-[var(--lux-panel)]">
                  <img src={article.cover} alt={article.imageAlt} width="1200" height="800" className="w-full h-full object-cover" loading="eager" fetchPriority="high" data-testid="article-cover" />
                </div>
                <figcaption className="mt-2 text-[11px] text-white/35 text-right">{copy.coverCredit}</figcaption>
              </figure>
            </div>
          </Container>
        </header>

        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-8">
          <aside className="lg:col-span-4 order-first lg:order-last">
            <div className="lg:sticky lg:top-24 space-y-4">
              <TableOfContents title={copy.toc} sections={article.sections} testId="article-toc" />
              <div className="rounded-2xl border border-lux-gold/25 bg-lux-gold/5 p-5" data-testid="article-guide-link">
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-2">{copy.inGuide}</p>
                <p className="text-sm text-white/65 leading-relaxed">{copy.inGuideText} {'\u00ab'} {article.axisName} {'\u00bb'}.</p>
                <Link to={guideHref} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200" data-testid="article-guide-cta">
                  <BookOpen className="w-4 h-4" /> {copy.inGuideCta}
                </Link>
              </div>
            </div>
          </aside>
          <div className="lg:col-span-8 max-w-3xl" data-testid="article-body">
            {article.sections.map(section => (
              <section key={section.id} id={section.id} className="scroll-mt-24 pt-8 first:pt-0" aria-labelledby={`${section.id}-title`} data-testid={`article-section-${section.id}`}>
                <h2 id={`${section.id}-title`} className="font-display text-2xl sm:text-3xl text-white">{section.title}</h2>
                <ContentBlocks blocks={section.blocks} testIdPrefix={`article-${section.id}`} />
              </section>
            ))}

            <ArticleShareBar title={article.title} path={article.path} lang={lang} className="mt-10 pt-6 border-t border-white/[0.08]" testIdPrefix="article-share-bottom" />

            <nav className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4" aria-label="Pagination" data-testid="article-pagination">
              {prev ? (
                <Link to={prev.path} className="rounded-2xl border border-white/10 bg-lux-panel/90 p-4 hover:border-lux-gold/40 transition-[border-color] duration-200" data-testid="article-prev">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> {copy.prev}</span>
                  <p className="mt-1 text-sm font-medium text-white">{prev.title}</p>
                </Link>
              ) : <span />}
              {next && (
                <Link to={next.path} className="rounded-2xl border border-white/10 bg-lux-panel/90 p-4 hover:border-lux-gold/40 transition-[border-color] duration-200 sm:text-right" data-testid="article-next">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 inline-flex items-center gap-1">{copy.next} <ArrowRight className="w-3 h-3" /></span>
                  <p className="mt-1 text-sm font-medium text-white">{next.title}</p>
                </Link>
              )}
            </nav>
          </div>
        </Container>

        <section className="py-12 border-t border-white/[0.06]" aria-label={copy.related}>
          <Container>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="font-display text-2xl sm:text-3xl text-white">{copy.related}</h2>
              <Link to={pagePath('tips', lang)} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200" data-testid="article-all-tips">{copy.allTips} <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="article-related">
              {related.map(a => <ArticleCard key={a.key} article={a} lang={lang} compact />)}
            </div>
          </Container>
        </section>
      </article>

      <CtaBand title={copy.ctaTitle} sub={copy.ctaText} ctaLabel={copy.ctaLabel} to={appHref} testId="article-final-cta" />
    </PublicLayout>
  );
}
